import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Notification } from "@rio-cloud/rio-uikit";
import moment from "moment";

import { Trip } from "src/types/trips";
import { mountDashboardTrips } from "./mountDashboardTrips";

import {
  fetchCanceledTrip,
  fetchTripsList,
  sendStatusSchulingTrip,
  startTrip,
} from "src/services/trips";
import { fetchDetail } from "src/services/routes";

import {
  clearTrip,
  setDriversSelected,
  setIdRouteSelected,
  setIdTripSelected,
  setIdVehicleSelected,
  setNameVehicleSelected,
  setScheduleDateSelected,
  setScheduleHoursSelected,
  setTripUpdateDetailData,
  setTripUpdateRouteCostData
} from "@store";

import { ConfigPaginator } from "src/components/PaginatorSize";
import { ICsvData } from "../types";

export const useTripsController = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();

  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState<{ [name: string]: string }>({ "scheduling": "desc" });
  const [fieldSort, setFieldSort] = useState("scheduling");
  const [openModal, setOpenModal] = useState(false);
  const [show, setShow] = useState(false);
  const [idDelete, setIdDelete] = useState<undefined | string>("");
  const [dashboard, setDashboard] = useState({ allTrips: 0, scheduled: 0, new: 0, pending: 0, refused: 0, canceled: 0, acomplished: 0 });
  const [filterByType, setFilterByType] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [trips, setTrips] = useState<Trip[]>([]);

  const [totalTrips, setTotalTrips] = useState(0);
  const page = search.get("page") ? Number(search.get("page")) : 1;
  const [size, setSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [idStartTrip, setIdStartTrip] = useState<string>("");

  const searchTrips = async () => {
    try { 
      const { data: { result } } = await fetchTripsList(
        1,
        size,
        searchValue,
        sort[fieldSort],
        fieldSort,
        filterByType,
        startDate,
        endDate
      );
      const dashboardData = mountDashboardTrips(result);
      const totalByFilter: Record<string, number> = {
        ALL: dashboardData.allTrips,
        ACCEPTED: dashboardData.scheduled,
        PROPOSED: dashboardData.new,
        PENDING: dashboardData.pending,
        REFUSED: dashboardData.refused,
        CANCELED: dashboardData.canceled,
        ACOMPLISHED: dashboardData.acomplished,
      };
      setTrips(result?.trips);
      setDashboard(dashboardData);
      setTotalTrips(totalByFilter[filterByType] ?? dashboardData.allTrips);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      Notification.error("Não foi possível buscar as viagens.");
      return { trips: [], pages: 0, result: [] };
    }
  }

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleUpdateRoute = async (trip: Trip) => {
    dispatch(setTripUpdateDetailData(undefined));
    dispatch(setTripUpdateRouteCostData(undefined));
    dispatch(setIdTripSelected(trip.id || ""));

    const driversToSelect = Array.isArray(trip.dispatchedDrivers) && trip.dispatchedDrivers.length > 0
      ? trip.dispatchedDrivers.map(d => ({
          id: d.driverId,
          uniqueId: d.profileId || "",
          name: d.driverName,
          avatar: "",
        }))
      : [{
          id: trip.driverId || "",
          uniqueId: trip.profileId || "",
          name: trip.driverName || "",
          avatar: "",
        }];
    dispatch(setDriversSelected(driversToSelect));
    dispatch(setIdVehicleSelected(trip.assetId || ""));
    dispatch(setNameVehicleSelected(trip.assetName || ""));
    dispatch(setIdRouteSelected(trip.route?.id || ""));
    dispatch(setScheduleDateSelected(moment(trip.schedule?.date.toString()).format("YYYY-MM-DD") || ""));
    dispatch(setScheduleHoursSelected(trip.schedule?.date ? moment(trip.schedule?.date.toString()).format("HH:mm") : ""));

    const currentParams = new URLSearchParams(search);
    currentParams.set("id", trip.route?.id || "");
    currentParams.set('editTrip', 'true');

    let dnGroupId = trip.emergencyInfo?.dnGroupId;
    let dnGroupName = trip.emergencyInfo?.dnGroupName;

    if (!dnGroupId && trip.route?.id) {
      try {
        const detail = await fetchDetail(trip.route.id);
        dnGroupId = detail?.data?.result?.emergencyInfo?.dnGroupId ?? undefined;
        dnGroupName = detail?.data?.result?.emergencyInfo?.dnGroupName ?? undefined;
      } catch {
        dnGroupId = undefined;
      }
    }

    if (dnGroupId) {
      currentParams.set("dn-group-id", dnGroupId);
      if (dnGroupName) {
        currentParams.set("dn-group-name", dnGroupName);
      }
    }

    const url = "/routing/trips/update?" + currentParams.toString();
    navigate(url, { state: { edit: true } });
  };

  const handleCanceled = async () => {
    try {
      const date = new Date().toISOString();
      await fetchCanceledTrip({ deletedAt: date, updatedAt: date }, idDelete as string);
      await searchTrips();
      setShow(false);
    } catch (error) {
      Notification.error("Não foi possível buscar as rotas.");
    }
  };

  const handleShowTripDetails = (driverId: string, tripId: string, status: string) => {
    navigate("/routing/trips/performance", { state: { driverId, tripId, page, status: status} });
  };

  const handleDownload = () => {
  };

  const handlePage = (page: ConfigPaginator) => {
    setSize(page.next);
  }

  const handleSchedulingActionDate = async (status: "ACCEPT" | "REFUSE", tripId: string) => {
    try {
      await sendStatusSchulingTrip({ status, tripId });
      const statusEnum = { ACCEPT: "aceito", REFUSE: "recusado" };
      Notification.success(`Agendamento ${statusEnum[status]} com sucesso`);
      await searchTrips();
    } catch (error) {
      const statusEnum = { ACCEPT: "Não foi possível aceitar no momento.", REFUSE: "Não foi possível recusar no momento." };
      Notification.success(statusEnum[status]);
    }
  };

  const handleSelectTripFilterByType = async (type: string) => {
    setFilterByType(prev => {
      if (prev === type) {
        return type === 'ALL' ? 'ALL' : 'ALL';
      }
      return type;
    });
  };


  const handleSelectFileModel = (json?: ICsvData[]) => {
    try {
      if (!json?.length) {
        Notification.error("Não foi possível processar os dados do arquivo CSV.");
        return;
      }

      navigate("/routing/trips/schedule", {
        state: {
          edit: false,
          trip: false,
          csvData: json,
          csv: true,
        }
      });
    } catch (error) {
      Notification.error("Não foi possível processar os dados do arquivo CSV.");
    }
  };

  const handleSelectTrip = () => {
    dispatch(clearTrip());
    navigate("/routing/trips/routes", {
      state: {
        edit: false,
        trip: false,
      }
    });
  };

  const handleSelectNewRoute = () => {
    dispatch(clearTrip());
    navigate("/routing/trips/schedule", {
      state: {
        edit: false,
        trip: false,
      }
    });
  };

  const handleStartTripConfirm = (id: string) => {
    setIdStartTrip(id);
    setShowStartConfirm(true);
  };

  const handleStartTrip = async () => {
    try {
      await startTrip(idStartTrip);
      Notification.success("Viagem iniciada com sucesso!");
      await searchTrips();
      setShowStartConfirm(false);
    } catch (error) {
      Notification.error("Não foi possível iniciar a viagem.");
    }
  };

  const handleSearchValueChange = async (text: string) => {
    setSize(10);
    setSearchValue(text);
  }

  const handleDateFilterChange = async (start?: string, end?: string) => {
    // filtro por data
    setStartDate(start);
    setEndDate(end);
  };

  const handleResetPage = () => {
    console.log()
  }

  const handleResetDate = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };


  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      searchTrips();
    }, 500);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [searchValue, size, startDate, endDate, filterByType, sort, fieldSort]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      searchTrips();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [searchValue, size, startDate, endDate, filterByType, sort, fieldSort]);

  return {
    trips,
    dashboard,
    searchValue,
    sort,
    fieldSort,
    openModal,
    filterByType,
    show,
    idDelete,
    setSearchValue,
    setSort,
    setFieldSort,
    handleOpenModal,
    handleCloseModal,
    handleUpdateRoute,
    handleSelectTrip,
    handleSelectFileModel,
    handleShowTripDetails,
    handleDownload,
    handleSchedulingActionDate,
    handleSelectTripFilterByType,
    setShow,
    setIdDelete,
    handleCanceled,
    setSearch,
    handlePage,
    handleDateFilterChange,
    handleSearchValueChange,
    size,
    totalSize: totalTrips,
    handleResetPage,
    handleResetDate,
    isLoading,
    handleSelectNewRoute,
    showStartConfirm,
    setShowStartConfirm,
    handleStartTripConfirm,
    handleStartTrip,
  };
}

